import { registerBrick, Config, ErrorFlow, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import React from 'react';
import { GridActionsCellItem } from '@mui/x-data-grid';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { DataGridPro, LicenseInfo, GridToolbar } from '@mui/x-data-grid-pro';
import { ThemeProvider } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import { datagridTheme } from '../shared/_theme.jsx';
import { AddDialog } from '../shared/AddDialog.jsx';
import { Alert } from '../shared/Alert.jsx';
import { ChangeErrorAlert } from '../shared/ChangeErrorAlert.jsx';

export default class MissingConfigurationBrick extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): React.ReactElement}
     */
    static getReactComponent($) {
        return (props) => {
            // listen to properties
            const loading = useProperty($, 'Loading');
            let data = useProperty($, 'Data') || [];
            const sortBy = useProperty($, 'Sort By');
            const canEdit = useProperty($, 'Can edit');
            const addApprover = $.get('Post Add Approver');
            const [selectedRow, setSelectedRow] = React.useState();
            const [addingApprover, setAddingApprover] = React.useState(false);
            const [openAlert, setOpenAlert] = React.useState(false);
            const [openErrorAlert, setOpenErrorAlert] = React.useState(false);
            const [errorMessage, setErrorMessage] = React.useState('');

            console.log('MissingConfigurationData', data);

            // setup data grid, columns to display
            const licenseKey = Config.getParameter('uiappValues.datagrid_license');
            LicenseInfo.setLicenseKey(licenseKey);

            const costObjectSortComparator = (id1, id2) => {
                return Number.parseInt(id1) - Number.parseInt(id2);
            };

            const openDocs = React.useCallback(
                (row) => () => {
                    $.set('Selected Row', row);
                    $.trigger('On Open Documents');
                },
                []
            );

            const startAddingApprover = (row) => {
                console.log('row', row);
                if (row.Bstyp === 'F' || row.Bstyp === 'K') {
                    // PO
                    $.set('Operation', 'PO');
                } else if (row.Bstyp === 'B') {
                    // PR
                    $.set('Operation', 'PR');
                }

                setSelectedRow(row);
                setAddingApprover(true);
            };

            const handleAddApprover = (approverAdded, addedOrderCategory, type) => (event) => {
                // eslint-disable-next-line no-unused-vars
                const [triggerInput, itemIdInput, relGroup, relCode, orderCategory, agentType, userId, positionId] =
                    addApprover.getInputs();

                const [Success, Error] = addApprover.getOutputs();

                const addApprover$ = $.runner(addApprover)
                    .set(itemIdInput, $.get('Operation') === 'PO' ? selectedRow.Ekgrp : selectedRow.CostObject)
                    .set(relGroup, selectedRow.Frggr)
                    .set(relCode, selectedRow.Frgco)
                    .set(orderCategory, addedOrderCategory?.Auftyp)
                    .set(agentType, type === 'US' ? 'US' : 'S')
                    .set(userId, approverAdded.ApproverId || approverAdded.PosId)
                    .set(positionId, approverAdded.PositionId || approverAdded.PosId)
                    .trigger(triggerInput);

                Promise.any([addApprover$.waitFor(Success), addApprover$.waitFor(Error)]).then(
                    handleDialogChangeResponse
                );
            };

            const handleDialogChangeResponse = (val) => {
                if (val instanceof ErrorFlow) {
                    setErrorMessage(val.getMessage());
                    setOpenErrorAlert(true);
                    $.set('Error message', val.getMessage());
                    $.trigger('On Adding Approver failed');
                } else {
                    setAddingApprover(false);
                    setOpenAlert(true);
                    $.trigger('On Approver added');
                }
            };

            const handleCloseAlert = () => {
                setOpenAlert(false);
                setOpenErrorAlert(false);
                setErrorMessage('');
            };

            const columns = [];
            const defineColumn = (field, headerName, otherProps) => {
                const col = {
                    field,
                    headerName,
                    flex: 1,
                    resizable: false,
                    disableColumnMenu: true,
                    sortable: true,
                    filterable: true,
                    hideable: true,
                    pinnable: false,
                    ...otherProps,
                };
                if (field === 'docError') {
                    col.type = 'actions';
                    col.sortable = false;
                    col.getActions = (params) => [
                        <Badge badgeContent={params.row.DocCount} color="primary" key={params.id}>
                            <GridActionsCellItem
                                icon={<AssignmentIcon />}
                                onClick={openDocs(params.row)}
                                label="View Documents"
                            />
                        </Badge>,
                    ];
                }
                if (field === 'actions') {
                    col.type = 'actions';
                    col.sortable = false;
                    col.getActions = (params) => [
                        canEdit &&
                        (params.row.BstypDocType?.startsWith('B') ||
                            params.row.BstypDocType?.startsWith('F') ||
                            params.row.BstypDocType?.startsWith('K')) ? (
                            <Button onClick={() => startAddingApprover(params.row)}>Add Approver</Button>
                        ) : (
                            <span></span>
                        ),
                    ];
                }
                columns.push(col);
                return col;
            };

            const hyperlink = (url, data) => {
                $.set('Hyperlink', { url, data });
                $.trigger('On Hyperlink clicked');
            };

            const renderCellWithLink = (cell) => {
                const { row, field, value } = cell;
                const data = {
                    releaseGroup: row.Frggr,
                    releaseCode: row.Frgco,
                };
                // Purchase Order hyperlinks will be enabled when the new puchase order tile will be enabled
                // eslint-disable-next-line no-constant-condition
                if (
                    canEdit &&
                    (row.BstypDocType?.startsWith('F') || row.BstypDocType?.startsWith('K')) &&
                    field === 'Ekgrp'
                ) {
                    const url = '/purchase-group';
                    data.purchaseGroup = row.Ekgrp;
                    return (
                        <span className="dg-cell-link" onClick={() => hyperlink(url, data)}>
                            {value}
                        </span>
                    );
                } else if (canEdit && row.BstypDocType?.startsWith('B') && field === 'CostObject') {
                    const isCostCenter =
                        row.CostObjectType === 'Cost Center' || row.CostObjectType === 'Stat.WBS-Cost Center';
                    const url = isCostCenter ? '/cost-center' : '/wbs';
                    data[isCostCenter ? 'costCenter' : 'wbs'] = row.CostObject;
                    return (
                        <span className="dg-cell-link" onClick={() => hyperlink(url, data)}>
                            {value}
                        </span>
                    );
                }
                return <span>{value}</span>;
            };

            defineColumn('Frggr', 'Release Group');
            defineColumn('Frgco', 'Release Code');
            defineColumn('CostObject', 'Cost Object', {
                flex: 1.5,
                cellClassName: 'MuiDataGrid-cellBold',
                sortComparator: costObjectSortComparator,
                renderCell: renderCellWithLink,
            });
            defineColumn('CostObjectType', 'Cost Object Type', { flex: 2, sortComparator: costObjectSortComparator });
            defineColumn('Ekgrp', 'Purchasing Group', { flex: 2, renderCell: renderCellWithLink });
            defineColumn('BstypDocType', 'Document Category', { flex: 2 });
            defineColumn('docError', 'Documents in Error');
            if (canEdit) {
                defineColumn('actions', 'Actions');
            }

            // check if a column should be used for sorting
            let sortModel;
            const asc = true;
            if (sortBy && columns.some((c) => c.field === sortBy)) {
                sortModel = [{ field: sortBy, sort: asc ? 'asc' : 'desc' }];
            } else {
                sortModel = undefined;
            }

            // process data, add unique index prop
            if (!loading) {
                data.forEach((o, i) => {
                    o.id = i;
                });
            } else {
                data = [];
            }

            // render
            return (
                <ThemeProvider theme={datagridTheme}>
                    <DataGridPro
                        columns={columns}
                        rows={data}
                        loading={loading}
                        sortModel={sortModel}
                        editMode="row"
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: { style: { height: '40px' } }, // give enough room for the filter pad number to display entirely
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaderTitle': {
                                textOverflow: 'clip',
                                whiteSpace: 'break-spaces',
                                lineHeight: 1,
                            },
                        }}
                        scrollbarSize={17}
                    />
                    {addingApprover && (
                        <AddDialog
                            open={open}
                            title={$.get('Operation') === 'PO' ? 'Purchase Group' : 'Cost Center'}
                            itemDescription={null}
                            itemId={$.get('Operation') === 'PO' ? selectedRow.Ekgrp : selectedRow.CostObject}
                            childAccordionDetails={{
                                RelGroup: selectedRow.Frggr,
                                RelCode: selectedRow.Frgco,
                            }}
                            handleCloseDialog={() => setAddingApprover(false)}
                            handleAddApprover={handleAddApprover}
                            context={$}
                        />
                    )}
                    {openAlert && <Alert open={openAlert} handleClose={handleCloseAlert} />}
                    {openErrorAlert && (
                        // TODO replace with Show Backend Alert brick call (from relevant lambda) to simplify code
                        <ChangeErrorAlert
                            open={openErrorAlert}
                            handleClose={handleCloseAlert}
                            errorMessage={errorMessage}
                        />
                    )}
                </ThemeProvider>
            );
        };
    }
}

registerBrick('0189998069ba1b4c5233', MissingConfigurationBrick);
